import { useMemo, useState } from "react";
import useSWR from "swr";
import { type SWRConfiguration } from "swr";
import * as Sentry from "@sentry/react";
import { isAxiosError } from "axios";
import { useApiClient } from "@/hooks/useApiClient";
import type { BulkResolveResponse } from "@/features/propertyCodeBulkAcquisition/types";
import {
  BULK_RESOLVE_ENDPOINT,
  postBulkResolve,
} from "@/features/propertyCodeBulkAcquisition/api/postBulkResolve";
import { type DatalensBulkPropertyGroup } from "@/features/propertyCodeBulkAcquisition/utils/bulkPropertyGroups";
import {
  chunkResolveGroups,
  countUniqueCodes,
} from "@/features/propertyCodeBulkAcquisition/utils/chunkResolveGroups";

// リユース可否の判定が加わり照合の所要時間がコード数に比例して伸びたため、
// 1回のPOSTで全件を照合せず分割してワーカーの長時間占有とタイムアウトを避ける
const RESOLVE_CHUNK_SIZE = 5000;

/**
 * 照合フックの SWR オプション。
 * - revalidateOnFocus/Reconnect false: 照合は最大5万コードの POST で、
 *   入力（sessionStorage の受け渡しデータ）が変わらない限り結果も変わらないため再発行しない。
 * - shouldRetryOnError false: 400 等で再試行ループしない。
 * - onError: グローバル SWRConfig.onError（App.tsx）の汎用トーストを抑止し、
 *   エラーは error として返して画面内 Alert 側で表示する。400 系バリデーションを除く
 *   予期しない失敗のみ Sentry へ送る。
 */
const BULK_RESOLVE_SWR_OPTIONS: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  shouldRetryOnError: false,
  onError: (error: unknown) => {
    if (
      isAxiosError(error) &&
      (error.response == null || error.response.status >= 500)
    ) {
      Sentry.captureException(error);
    }
  },
};

interface UseBulkPropertyResolveReturn {
  resolveResult: BulkResolveResponse | undefined;
  isLoading: boolean;
  error: Error | undefined;
  /** 照合の進捗（処理済みコード数 / ユニークコード総数）。未開始時は 0 */
  progress: { processedCodes: number; totalCodes: number };
}

/**
 * 受け渡された所有者グループの物件コード群を照合 API で一括解決する。
 * POST /property-codes/resolve（apiClient の baseURL が /api を付与）。
 * groups が null（読み出し前・受け渡しデータなし）の間は SWR の key を null にして発火させない。
 * @param groups 所有者別の物件コードリスト。未確定時は null。
 */
export const useBulkPropertyResolve = (
  groups: DatalensBulkPropertyGroup[] | null
): UseBulkPropertyResolveReturn => {
  const { apiClient } = useApiClient();
  const [processedCodes, setProcessedCodes] = useState(0);
  const totalCodes = useMemo(
    () => (groups == null ? 0 : countUniqueCodes(groups)),
    [groups]
  );

  const { data, error, isLoading } = useSWR<BulkResolveResponse, Error>(
    groups != null ? [BULK_RESOLVE_ENDPOINT, groups] : null,
    async ([, g]: [string, DatalensBulkPropertyGroup[]]) => {
      const merged: BulkResolveResponse = {
        resolvedCount: 0,
        unresolvedCount: 0,
        properties: [],
        unresolvedPropertyCodes: [],
      };
      let processed = 0;
      for (const chunk of chunkResolveGroups(g, RESOLVE_CHUNK_SIZE)) {
        const request = {
          propertyGroups: chunk.map((group) => ({
            name: group.name,
            propertyCodes: group.codes,
          })),
        };
        // apiClient は 401 でトークンを再設定するが失敗したリクエスト自体は再実行しない。
        // 受け渡しデータは読み出し時に消費済みで照合をやり直せないため、401 のみ1回だけやり直す
        const result = await postBulkResolve(apiClient, request).catch(
          async (e: unknown) => {
            if (!isAxiosError(e) || e.response?.status !== 401) {
              throw e;
            }
            return await postBulkResolve(apiClient, request);
          }
        );
        merged.resolvedCount += result.resolvedCount;
        merged.unresolvedCount += result.unresolvedCount;
        merged.properties.push(...result.properties);
        merged.unresolvedPropertyCodes.push(...result.unresolvedPropertyCodes);
        processed += result.resolvedCount + result.unresolvedCount;
        setProcessedCodes(processed);
      }
      return merged;
    },
    BULK_RESOLVE_SWR_OPTIONS
  );

  return {
    resolveResult: data,
    isLoading,
    error,
    progress: { processedCodes, totalCodes },
  };
};
